<template>
  <div class="border-b bg-white p-4">
    <div class="space-y-4">
      <div class="grid auto-cols-fr grid-flow-col items-center">
        <div class="col-span-2 flex items-center justify-start gap-x-2">
          <call-disposition-selector
            :log-data="log.data"
            :show-call-text="showCallText || !isInboxPage"
          />
          <business-name
            v-if="isInboxPage"
            :id="log.id"
            class="max-w-fit"
            :name="log.business_legal_name"
            :application-id="log.application_id"
            :type="log.type"
            @click.stop
          />
          <img :src="`/images/${providerImage}`" class="h-5 w-5" />
        </div>
        <div v-if="!hasTextData" class="justify-self-end">
          <div class="flex items-center space-x-2">
            <date-time
              v-if="log.data?.created_at && isInboxPage"
              :date="log.data.created_at"
              show-time-if-today
              class="truncate text-xs"
            />
            <div
              v-tooltip="textVisibilityToggleTooltip"
              :data-cy="
                showCallText
                  ? 'hide-summary-button-no-recording'
                  : 'show-summary-button-no-recording'
              "
              class="mt-1 cursor-pointer rounded p-2 text-label hover:bg-grey-hover"
              @click.prevent="showCallText = !showCallText"
            >
              <icon-base :icon="showCallText ? IconMinus : IconPlusSmall" />
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-y-2">
        <div
          v-if="callerInfo.from && isInboxPage"
          class="flex items-center gap-x-2"
        >
          <span
            v-if="callerInfo.from.name"
            data-cy="from-name"
            class="font-semibold text-headline"
          >
            {{ callerInfo.from.name }}
          </span>
          <span v-if="callerInfo.from.number" data-cy="from-number">
            ({{ callerInfo.from.number }})
          </span>
        </div>
        <div
          v-if="callerInfo.to && isInboxPage"
          class="flex items-center gap-x-2"
        >
          <span v-if="callerInfo.to.name" data-cy="to-name">
            {{ $t("COMMON.TO") }} {{ callerInfo.to.name }}
          </span>
          <span v-if="callerInfo.to.number" data-cy="to-number">
            ({{ callerInfo.to.number }})
          </span>
        </div>
      </div>
      <lazy-load :options="lazyLoadOptions" persist class="call-recording">
        <call-log-recording
          v-if="log.data.recording"
          :id="log.id"
          :session-id="log.data.id"
          :recording="log.data.recording"
        />
      </lazy-load>
      <lazy-load :options="lazyLoadOptions" persist>
        <call-recording-text
          v-if="showCallText || hasTextData"
          :call-disposition="callLogData.call_disposition"
          :call-log="log"
        />
      </lazy-load>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { CommunicationProvider } from "@/enums/communicationProviders";
import { useCommunicationLogs } from "@/hooks/communicationLogs";

import LazyLoad from "@/components/LazyLoad.vue";
import CallDispositionSelector from "@/components/communicationLogs/communication/CallDispositionSelector.vue";
import CallLogRecording from "@/components/communicationLogs/communication/CallLogRecording.vue";
import CallRecordingText from "@/components/communicationLogs/communication/CallRecordingText.vue";
import IconMinus from "@/components/icons/IconMinus.vue";
import IconPlusSmall from "@/components/icons/IconPlusSmall.vue";
import BusinessName from "@/components/inbox/BusinessName.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { LazyLoadOptions } from "@/models/common";
import type { CallLog } from "@/models/communicationLogs";

interface Props {
  log: CallLog;
}

const { log } = defineProps<Props>();

const { t } = useI18n();
const { isInboxPage } = useCommunicationLogs();

const lazyLoadOptions: LazyLoadOptions = {
  style: {},
};

const showCallText = ref(false);

const hasTextData = computed(() => {
  const { body, has_summary, has_transcription } = log.data;

  return !!body || !!has_summary || !!has_transcription;
});

const textVisibilityToggleTooltip = computed(() => {
  return showCallText.value
    ? `${t("COMMON.CLOSE")} ${t("COMMON.SUMMARY")}`
    : `${t("COMMON.SHOW")} ${t("COMMON.SUMMARY")}`;
});

const callLogData = computed(() => log.data);

const callerInfo = computed(() => {
  const log = callLogData.value;
  const { from, to, is_outgoing } = log;
  const { full_name } = log.user ?? {};
  const { name } = log.callee ?? {};
  const fromInfo = is_outgoing
    ? { name: full_name, number: from }
    : { name, number: to };
  const toInfo = is_outgoing
    ? { name, number: to }
    : { name: full_name, number: from };
  return { from: fromInfo, to: toInfo };
});

const providerImage = computed(() =>
  callLogData.value.provider === CommunicationProvider.Twilio
    ? "twilio.svg"
    : "ringcentral.svg"
);
</script>
<style>
@reference "@/assets/tailwind.css";

.call-disposition-selector {
  flex: none !important;
  .dropdown {
    @apply p-2;
  }
}
</style>
